import { useState } from 'react';
import { TrashIcon, ArrowDownTrayIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { Button, Modal, ModalFooter } from '@/components/common';

interface BulkActionBarProps {
  selectedCount: number;
  onDelete: () => void;
  onExport: () => void;
  onActivate?: () => void;
  onDeactivate?: () => void;
}

export function BulkActionBar({
  selectedCount,
  onDelete,
  onExport,
  onActivate,
  onDeactivate,
}: BulkActionBarProps) {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
    setIsDeleteConfirmOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-800 px-4 py-2">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
          {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
        </span>
        <div className="h-4 w-px bg-primary-200 dark:bg-primary-700" />
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<TrashIcon className="w-4 h-4" />}
          onClick={handleDeleteClick}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          Delete
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          onClick={onExport}
        >
          Export CSV
        </Button>
        {onActivate && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<PlayIcon className="w-4 h-4 text-green-600" />}
            onClick={onActivate}
          >
            Activate
          </Button>
        )}
        {onDeactivate && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<PauseIcon className="w-4 h-4 text-yellow-600" />}
            onClick={onDeactivate}
          >
            Deactivate
          </Button>
        )}
      </div>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Delete Selected Items"
        size="sm"
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete {selectedCount} selected item
          {selectedCount !== 1 ? 's' : ''}? This action cannot be undone.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete {selectedCount} Item{selectedCount !== 1 ? 's' : ''}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
